// src/pages/ProvidersPage.jsx
import React, { useEffect, useState, useMemo } from "react";
import { getMyProviders, addProvider } from "../lib/providerApi";
import "./ProvidersPage.css";

const ALL_PACKAGES = [
  {
    id: "free",
    label: "Free",
    subtitle: "Basispakket",
    description:
      "Je kunt sessies starten en joinen die alleen het Free pakket nodig hebben.",
  },
  {
    id: "sports",
    label: "Sports",
    subtitle: "Uitgebreid sportpakket",
    description:
      "Je kunt sessies starten en joinen die Free óf Sports nodig hebben.",
  },
  {
    id: "all_access",
    label: "All access",
    subtitle: "Volledige toegang",
    description:
      "Je kunt alle sessies starten en joinen, ongeacht welk pakket ze nodig hebben.",
  },
];

// Testpakketten hiërarchie
const PACKAGE_LEVELS = {
  free: 0,
  sports: 1,
  all_access: 2,
};

function normalizeId(id) {
  return String(id || "").toLowerCase();
}

function getPackageMeta(providerId) {
  const norm = normalizeId(providerId);
  return ALL_PACKAGES.find((p) => p.id === norm) || null;
}

function getHighestTestPackage(providers) {
  let best = null;
  let bestLevel = -1;

  for (const p of providers || []) {
    const meta = getPackageMeta(p.providerId);
    if (!meta) continue;
    const level = PACKAGE_LEVELS[meta.id] ?? -1;
    if (level > bestLevel) {
      bestLevel = level;
      best = meta;
    }
  }

  if (!best) return null;
  return {
    meta: best,
    level: bestLevel,
  };
}

function ProvidersPage({ currentUser, token }) {
  const safeToken = token || null;
  const user = currentUser || null;

  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!safeToken) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const list = await getMyProviders(safeToken);
        if (!cancelled) {
          setProviders(list || []);
        }
      } catch (e) {
        console.error("getMyProviders error", e);
        if (!cancelled) {
          setError("Kon je pakketten niet laden.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [safeToken]);

  const hasPackage = (id) =>
    providers.some(
      (p) => normalizeId(p.providerId) === normalizeId(id)
    );

  async function handleAddPackage(id) {
    if (busyId || !safeToken) return;
    setBusyId(id);
    setError("");

    try {
      const added = await addProvider(id, safeToken);
      setProviders((prev) => {
        const exists = prev.find(
          (p) =>
            normalizeId(p.providerId) === normalizeId(added.providerId)
        );
        if (exists) return prev;
        return [...prev, added];
      });
    } catch (e) {
      console.error("addProvider error", e);
      setError("Koppelen mislukt. Probeer het later opnieuw.");
    } finally {
      setBusyId(null);
    }
  }

  const highestTestPackage = useMemo(
    () => getHighestTestPackage(providers),
    [providers]
  );

  // Geen token → simpele state
  if (!safeToken) {
    return (
      <div className="providers-page">
        <div className="providers-inner">
          <h1 className="providers-title">Mijn pakketten</h1>
          <p className="providers-intro">
            Log eerst in om pakketten te kunnen koppelen.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="providers-page">
      <div className="providers-inner">
        {/* Titel + intro */}
        <h1 className="providers-title">Mijn pakketten</h1>

        <p className="providers-intro">
          Hier geef je aan welke pakketten / abonnementen jij hebt bij je
          sport-/TV-provider. Bond gebruikt dit later om te bepalen aan welke
          sessies je mag deelnemen. In deze fase werken we met testpakketten{" "}
          <span className="providers-intro-accent">Free</span>,{" "}
          <span className="providers-intro-accent">Sports</span>, en{" "}
          <span className="providers-intro-accent">All access</span>.
        </p>

        {user && (
          <p className="providers-user">
            Ingelogd als{" "}
            <span className="providers-user-name">{user.name}</span>{" "}
            <span className="providers-user-email">({user.email})</span>
          </p>
        )}

        {/* Huidig toegang-banner */}
        <section className="providers-access">
          <div className="providers-access-card">
            <div className="providers-access-header">Huidige toegang</div>
            {highestTestPackage ? (
              <div className="providers-access-body">
                <span>
                  Je hoogste pakket is{" "}
                  <span className="providers-access-strong">
                    {highestTestPackage.meta.label}
                  </span>
                  . Alle sessies met een lager pakket kun je joinen.
                </span>
              </div>
            ) : (
              <div className="providers-access-body">
                <span>
                  Je hebt nog geen testpakket gekoppeld. Je kunt alleen sessies
                  joinen die geen pakket vereisen.
                </span>
              </div>
            )}
            {highestTestPackage && (
              <div className="providers-access-pill">
                <span className="providers-access-pill-dot" />
                Actief pakket
              </div>
            )}
          </div>
        </section>

        {error && (
          <div className="providers-error-banner">
            {error}
          </div>
        )}

        {loading ? (
          <p className="providers-loading">Laden…</p>
        ) : (
          <>
            {/* Gekoppelde pakketten */}
            <section className="providers-section">
              <h2 className="providers-section-title">
                Gekoppelde pakketten
              </h2>

              {providers.length === 0 ? (
                <p className="providers-muted">
                  Je hebt nog geen pakketten gekoppeld.
                </p>
              ) : (
                <ul className="providers-linked-list">
                  {providers.map((p) => {
                    const meta = getPackageMeta(p.providerId);
                    const label = meta?.label || p.providerId;
                    const subtitle =
                      meta?.subtitle || "Gekoppeld pakket";

                    return (
                      <li
                        key={p.id}
                        className="providers-linked-item"
                      >
                        <div className="providers-linked-main">
                          <div className="providers-linked-label">
                            {label}
                          </div>
                          <div className="providers-linked-subtitle">
                            {subtitle}
                          </div>
                        </div>
                        <span className="providers-linked-badge">
                          gekoppeld
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            {/* Pakketten koppelen */}
            <section className="providers-section">
              <h2 className="providers-section-title">
                Pakketten koppelen
              </h2>
              <p className="providers-section-text">
                Dit is stap 1: aangeven welke pakketten jij hebt. De
                daadwerkelijke toegang tot wedstrijden blijft altijd via de
                officiële app/website van je provider. Bond gebruikt deze info
                alleen om te bepalen welke sessies je mag starten of joinen.
              </p>

              <div className="providers-grid">
                {ALL_PACKAGES.map((pkg) => {
                  const active = hasPackage(pkg.id);
                  const isBusy = busyId === pkg.id;

                  const cardClasses = [
                    "providers-package-card",
                    active && "providers-package-card--active",
                    isBusy && "providers-package-card--busy",
                  ]
                    .filter(Boolean)
                    .join(" ");

                  return (
                    <button
                      key={pkg.id}
                      type="button"
                      disabled={active || isBusy}
                      onClick={() => handleAddPackage(pkg.id)}
                      className={cardClasses}
                    >
                      <div className="providers-package-header">
                        <div className="providers-package-title">
                          {pkg.label}
                        </div>
                        {active && (
                          <div className="providers-package-status">
                            ✓ Gekoppeld
                          </div>
                        )}
                      </div>
                      <div className="providers-package-subtitle">
                        {pkg.subtitle}
                      </div>
                      <p className="providers-package-description">
                        {active
                          ? "Dit pakket staat al gekoppeld aan je Bondaccount."
                          : pkg.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

export default ProvidersPage;
